import { useEffect, useRef, useState } from "react";
import { Card, CardMedia, Typography, Box } from "@mui/material";
import apiService from "../../../../../../service/Api";

interface VideoPlayerProps {
  streamData: {
    id?: number;
    youtube_stream_link?: string;
    title?: string;
    type_of_service?: string;
  } | null;
  borderRadius?: string;
}

export const WATCH_MILESTONE_EVENT = "memorial:watch-milestone";
const LIVE_THRESHOLD_SECONDS = 1800; 
const VOD_COMPLETION_RATIO = 0.75;

interface YTPlayerInstance {
  getCurrentTime?: () => number;
  destroy?: () => void;
}

interface YTOnStateChangeEvent {
  data: number;
}

interface YTNamespace {
  Player: new (
    target: HTMLElement,
    options: {
      events: {
        onReady?: () => void;
        onStateChange: (event: YTOnStateChangeEvent) => void;
      };
    },
  ) => YTPlayerInstance;
  PlayerState: { PLAYING: number };
}

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let youtubeApiPromise: Promise<void> | null = null;

function loadYouTubeIframeAPI(): Promise<void> {
  if (window.YT?.Player) return Promise.resolve();
  if (youtubeApiPromise) return youtubeApiPromise;

  youtubeApiPromise = new Promise((resolve) => {
    const previousCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousCallback?.();
      resolve();
    };

    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(script);
  });

  return youtubeApiPromise;
}

type StreamPhase = "not_started" | "live" | "ended" | "unknown";

interface YouTubeStatus {
  phase: StreamPhase;
  duration_seconds: number | null;
}

function getThresholdSeconds(status: YouTubeStatus | null): number | null {
  if (!status) return null;

  if (status.phase === "ended" && status.duration_seconds) {
    return status.duration_seconds * VOD_COMPLETION_RATIO;
  }

  if (status.phase === "live" || status.phase === "unknown") {
    return LIVE_THRESHOLD_SECONDS;
  }

  return null;
}

export default function VideoPlayer({
  streamData,
  borderRadius = "20px",
}: VideoPlayerProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const playerRef = useRef<YTPlayerInstance | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const milestoneFiredRef = useRef(false);
  const [youtubeStatus, setYoutubeStatus] = useState<YouTubeStatus | null>(null);
  const youtubeStatusRef = useRef<YouTubeStatus | null>(null);
  youtubeStatusRef.current = youtubeStatus;

  const getYouTubeVideoId = (url: string) => {
    const regExp =
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regExp);
    return match && match[1] ? match[1] : null;
  };

  const videoId = streamData?.youtube_stream_link
    ? getYouTubeVideoId(streamData.youtube_stream_link)
    : null;

  const embedUrl = videoId
    ? `https://www.youtube.com/embed/${videoId}?autoplay=0&controls=1&modestbranding=1&rel=0&enablejsapi=1`
    : "about:blank";

  useEffect(() => {
    if (!streamData?.id) {
      setYoutubeStatus(null);
      return;
    }

    let cancelled = false;
    apiService
      .get<YouTubeStatus>(`/api/streams/${streamData.id}/youtube-status/`)
      .then((response) => {
        if (!cancelled) setYoutubeStatus(response.data);
      })
      .catch(() => {
        if (!cancelled) setYoutubeStatus({ phase: "unknown", duration_seconds: null });
      });

    return () => {
      cancelled = true;
    };
  }, [streamData?.id]);

  useEffect(() => {
    milestoneFiredRef.current = false;

    if (!videoId || !iframeRef.current || !streamData?.id) return;

    let cancelled = false;
    const streamId = streamData.id;

    loadYouTubeIframeAPI().then(() => {
      if (cancelled || !iframeRef.current || !window.YT) return;

      playerRef.current = new window.YT.Player(iframeRef.current, {
        events: {
          onStateChange: (event) => {
            const isPlaying = event.data === window.YT?.PlayerState.PLAYING;

            if (isPlaying) {
              if (pollRef.current) return;
              pollRef.current = setInterval(() => {
                if (!playerRef.current || milestoneFiredRef.current) return;

                const threshold = getThresholdSeconds(youtubeStatusRef.current);
                if (threshold === null) return;

                const currentTime = playerRef.current.getCurrentTime?.() ?? 0;
                if (currentTime >= threshold) {
                  milestoneFiredRef.current = true;
                  window.dispatchEvent(
                    new CustomEvent(WATCH_MILESTONE_EVENT, {
                      detail: { streamId, milestone: "watch-threshold" },
                    }),
                  );
                  if (pollRef.current) clearInterval(pollRef.current);
                }
              }, 10000);
            } else if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
          },
        },
      });
    });

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      playerRef.current?.destroy?.();
      playerRef.current = null;
    };
  }, [videoId, streamData?.id]);

  return (
    <Card
      sx={{
        // mb: { xs: 2, md: 4 },

        overflow: "hidden",
        boxShadow: 0,
        flexGrow: 1,
        width: "100%",
        borderRadius,
      }}
    >
      <Box
        sx={{
          position: "relative",
          aspectRatio: "16/9",
          bgcolor: "black",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {streamData?.youtube_stream_link ? (
          <CardMedia
            component="iframe"
            ref={iframeRef}
            src={embedUrl}
            title={streamData.title || "Memorial Service"}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: "none",
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : (
          <Typography variant="h6" color="white">
            Livestream link not available yet.
          </Typography>
        )}
      </Box>
    </Card>
  );
}
